// ServiceRevenueTab.tsx - Using Existing RevenueController API
import React, { useState, useEffect } from "react";
import { ColorTheme } from "../types/revenue.types";
import { KPICard } from "../shared/KPICard";
import RevenueService from "../../../services/revenueService";

interface ServiceRevenueTabProps {
  colorTheme: ColorTheme;
  startDate: string;
  endDate: string;
}

interface ServiceData {
  service_id: number;
  service_name: string;
  service_category: string;
  usage_count: number;
  total_revenue: number;
  avg_fee: number;
  provider_name: string;
}

export const ServiceRevenueTab: React.FC<ServiceRevenueTabProps> = ({
  colorTheme,
  startDate,
  endDate,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [services, setServices] = useState<ServiceData[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceData | null>(
    null,
  );

  useEffect(() => {
    loadServiceData();
  }, [startDate, endDate]);

  const loadServiceData = async () => {
    setLoading(true);
    try {
      // Use the existing working API!
      const data = await RevenueService.getServicePopularity({
        start_date: startDate,
        end_date: endDate,
      });
      setServices(data);
    } catch (error) {
      console.error("Error loading service revenue data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    return {
      totalRevenue: services.reduce(
        (sum, s) => sum + Number(s.total_revenue || 0),
        0,
      ),
      totalTransactions: services.reduce(
        (sum, s) => sum + Number(s.usage_count || 0),
        0,
      ),
      averageRevenue:
        services.length > 0
          ? services.reduce((sum, s) => sum + Number(s.total_revenue || 0), 0) /
            services.length
          : 0,
      serviceCount: services.length,
    };
  };

  const totals = calculateTotals();

  const getRevenueLevel = (revenue: number) => {
    if (revenue >= 1000)
      return { emoji: "🔥", color: colorTheme.success, label: "High" };
    if (revenue >= 500)
      return { emoji: "✅", color: colorTheme.primary, label: "Medium" };
    if (revenue >= 100)
      return { emoji: "📊", color: colorTheme.warning, label: "Low" };
    return { emoji: "💰", color: colorTheme.gray600, label: "Minimal" };
  };

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px",
          color: colorTheme.gray500,
        }}
      >
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>💰</div>
        <p>Loading service revenue data...</p>
      </div>
    );
  }

  return (
    <div>
      {/* KPI Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <KPICard
          icon="💰"
          title="Total Revenue"
          value={totals.totalRevenue}
          subtitle={`${services.length} services`}
          colorTheme={colorTheme}
        />
        <KPICard
          icon="📊"
          title="Total Transactions"
          value={totals.totalTransactions}
          subtitle="All services"
          colorTheme={colorTheme}
        />
        <KPICard
          icon="💵"
          title="Average Revenue"
          value={totals.averageRevenue}
          subtitle="Per service"
          colorTheme={colorTheme}
        />
        <KPICard
          icon="🔧"
          title="Active Services"
          value={totals.serviceCount}
          subtitle="With transactions"
          colorTheme={colorTheme}
        />
      </div>

      {/* Top 10 Services Table */}
      <div
        style={{
          background: "white",
          padding: "24px",
          borderRadius: "12px",
          marginBottom: "24px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: colorTheme.gray900,
            marginBottom: "16px",
          }}
        >
          🏆 Top Services by Revenue
        </h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${colorTheme.gray200}` }}>
                <th
                  style={{
                    textAlign: "left",
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.gray700,
                  }}
                >
                  Rank
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.gray700,
                  }}
                >
                  Service
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.gray700,
                  }}
                >
                  Provider
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.gray700,
                  }}
                >
                  Revenue
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.gray700,
                  }}
                >
                  Transactions
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.gray700,
                  }}
                >
                  Avg Fee
                </th>
              </tr>
            </thead>
            <tbody>
              {services
                .sort((a, b) => b.total_revenue - a.total_revenue)
                .slice(0, 10)
                .map((service, index) => {
                  const level = getRevenueLevel(service.total_revenue);
                  return (
                    <tr
                      key={service.service_id}
                      onClick={() => setSelectedService(service)}
                      style={{
                        borderBottom: `1px solid ${colorTheme.gray100}`,
                        cursor: "pointer",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = colorTheme.gray50)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td style={{ padding: "12px" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            background:
                              index === 0
                                ? "#fbbf24"
                                : index === 1
                                  ? "#d1d5db"
                                  : index === 2
                                    ? "#fb923c"
                                    : colorTheme.gray200,
                            color: index < 3 ? "white" : colorTheme.gray700,
                            fontSize: "14px",
                            fontWeight: "700",
                          }}
                        >
                          {index + 1}
                        </span>
                      </td>
                      <td style={{ padding: "12px" }}>
                        <div
                          style={{
                            fontWeight: "500",
                            color: colorTheme.gray900,
                          }}
                        >
                          {service.service_name}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: colorTheme.gray500,
                          }}
                        >
                          {service.service_category}
                        </div>
                      </td>
                      <td
                        style={{ padding: "12px", color: colorTheme.gray600 }}
                      >
                        {service.provider_name}
                      </td>
                      <td style={{ padding: "12px", textAlign: "right" }}>
                        <div style={{ fontWeight: "600", color: level.color }}>
                          {level.emoji}{" "}
                          {RevenueService.formatCurrency(service.total_revenue)}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          color: colorTheme.gray600,
                        }}
                      >
                        {service.usage_count}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          color: colorTheme.gray600,
                        }}
                      >
                        {RevenueService.formatCurrency(service.avg_fee)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* All Services List */}
      <div
        style={{
          background: "white",
          padding: "24px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: colorTheme.gray900,
            marginBottom: "16px",
          }}
        >
          📋 All Services - Revenue Details
        </h3>
        <div style={{ maxHeight: "600px", overflowY: "auto" }}>
          {services
            .sort((a, b) => b.total_revenue - a.total_revenue)
            .map((service) => {
              const level = getRevenueLevel(service.total_revenue);
              return (
                <div
                  key={service.service_id}
                  style={{
                    background: colorTheme.cardBg,
                    border: `1px solid ${colorTheme.gray200}`,
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "12px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => setSelectedService(service)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 4px 6px rgba(0, 0, 0, 0.1)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "12px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: colorTheme.gray900,
                          marginBottom: "4px",
                        }}
                      >
                        {service.service_name}
                      </div>
                      <div
                        style={{ fontSize: "13px", color: colorTheme.gray600 }}
                      >
                        {service.service_category} • {service.provider_name}
                      </div>
                    </div>
                    <div
                      style={{
                        background:
                          level.color === colorTheme.success
                            ? "#ecfdf5"
                            : level.color === colorTheme.primary
                              ? "#eff6ff"
                              : level.color === colorTheme.warning
                                ? "#fef3c7"
                                : colorTheme.gray100,
                        padding: "4px 12px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: level.color,
                      }}
                    >
                      {level.emoji}{" "}
                      {RevenueService.formatCurrency(service.total_revenue)}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(120px, 1fr))",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <div
                        style={{ fontSize: "12px", color: colorTheme.gray500 }}
                      >
                        Transactions
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: colorTheme.gray700,
                        }}
                      >
                        {service.usage_count}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{ fontSize: "12px", color: colorTheme.gray500 }}
                      >
                        Average Fee
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: colorTheme.primary,
                        }}
                      >
                        {RevenueService.formatCurrency(service.avg_fee)}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{ fontSize: "12px", color: colorTheme.gray500 }}
                      >
                        Revenue Level
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: level.color,
                        }}
                      >
                        {level.label}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {services.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: colorTheme.gray500,
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📊</div>
          <p>No service revenue data found for the selected period.</p>
        </div>
      )}
    </div>
  );
};
